import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, ChevronLeft, ChevronRight, Plus, Trash2, Archive, ArchiveRestore,
  Download, Upload, Filter, Loader2, ChevronDown, ChevronUp, X, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/apiClient";

const COLUMNS = [
  { key: "business_name", label: "Business", sortable: true, minW: 200 },
  { key: "contact_name", label: "Contact", sortable: true, minW: 140 },
  { key: "niche", label: "Niche", sortable: true, minW: 130 },
  { key: "phone", label: "Phone", sortable: false, minW: 130 },
  { key: "email", label: "Email", sortable: true, minW: 180 },
  { key: "website", label: "Website", sortable: false, minW: 180 },
  { key: "city", label: "City", sortable: true, minW: 120 },
  { key: "state", label: "State", sortable: true, minW: 80 },
  { key: "lead_score", label: "Score", sortable: true, minW: 80 },
  { key: "lead_source", label: "Source", sortable: true, minW: 140 },
  { key: "status", label: "Status", sortable: true, minW: 160 },
  { key: "created_at", label: "Added", sortable: true, minW: 130 },
];

const STATUS_COLORS = {
  "New Lead": "bg-white/5 text-white/70 border-white/15",
  "Contacted": "bg-blue-500/10 text-blue-200 border-blue-500/30",
  "Follow-Up Needed": "bg-amber-500/10 text-amber-200 border-amber-500/30",
  "Audit Requested": "bg-purple-500/10 text-purple-200 border-purple-500/30",
  "Audit Sent": "bg-purple-500/10 text-purple-200 border-purple-500/30",
  "Interested": "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
  "Meeting Booked": "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
  "Closed Won": "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  "Closed Lost": "bg-red-500/10 text-red-200 border-red-500/30",
  "Not Interested": "bg-white/5 text-white/40 border-white/10",
  "Do Not Contact": "bg-red-500/10 text-red-200 border-red-500/30",
};

const useDebounced = (v, ms = 400) => {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
};

export default function CRM() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "", niche: "", city: "", state: "", source: "", website_issue: "",
    min_score: "", max_score: "", archived: false,
  });
  const debSearch = useDebounced(search, 350);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [facets, setFacets] = useState({ statuses: [], niches: [], cities: [], states: [], sources: [], website_issues: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  // Auto-open create if ?new=1
  useEffect(() => {
    if (params.get("new") === "1") {
      setCreating(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
        search: debSearch || undefined,
        archived: filters.archived,
      };
      ["status", "niche", "city", "state", "source", "website_issue"].forEach((k) => {
        if (filters[k]) q[k] = filters[k];
      });
      if (filters.min_score !== "") q.min_score = filters.min_score;
      if (filters.max_score !== "") q.max_score = filters.max_score;

      const { data } = await api.get("/admin/leads", { params: q });
      setRows(data.items);
      setTotal(data.total);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, debSearch, filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/leads/facets");
        setFacets(data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (sortBy === col.key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col.key);
      setSortDir("desc");
    }
  };

  const toggleAll = () => {
    if (selected.size === rows.length && rows.length > 0) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const bulk = async (patch) => {
    if (selected.size === 0) return;
    try {
      await api.post("/admin/leads/bulk", { ids: Array.from(selected), ...patch });
      toast.success("Applied.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const confirmBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} lead(s)? This cannot be undone.`)) return;
    await bulk({ delete: true });
  };

  const exportCSV = async (onlySelected = false) => {
    const token = getToken();
    let url = `${API}/admin/leads/export.csv`;
    if (onlySelected && selected.size > 0) url += `?ids=${Array.from(selected).join(",")}`;
    if (filters.archived) url += (url.includes("?") ? "&" : "?") + "archived=true";
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "almonte_sites_leads.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created}, skipped ${data.duplicates_skipped} duplicate(s), ${data.invalid_skipped} invalid.`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
            CRM
          </span>
          <h1 className="font-display text-3xl md:text-4xl mt-2">
            {filters.archived ? "Archived leads" : "Active leads"}
          </h1>
          <p className="text-white/50 text-sm mt-1">
            {total.toLocaleString()} lead{total === 1 ? "" : "s"} · page {page} of {totalPages}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setCreating(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-10 px-4"
            data-testid="add-lead-btn"
          >
            <Plus size={15} className="mr-1.5" /> Add lead
          </Button>
          <Button
            onClick={() => fileRef.current?.click()}
            variant="outline"
            className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-full h-10 px-4"
            data-testid="import-csv-btn"
          >
            <Upload size={14} className="mr-1.5" /> Import CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} />
          <Button
            onClick={() => exportCSV(false)}
            variant="outline"
            className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-full h-10 px-4"
            data-testid="export-csv-btn"
          >
            <Download size={14} className="mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card-soft rounded-2xl p-4 md:p-5">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="crm-search"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search by business, contact, email, phone, city…"
              className="pl-9 bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-11 rounded-md focus-visible:ring-blue-500"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters((v) => !v)}
              className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-11 px-4"
              data-testid="toggle-filters"
            >
              <Filter size={14} className="mr-1.5" /> Filters
            </Button>
            <Button
              variant="outline"
              onClick={() => setFilters((f) => ({ ...f, archived: !f.archived }))}
              className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-11 px-4"
              data-testid="toggle-archived"
            >
              {filters.archived ? "Show active" : "Show archived"}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <FilterSelect label="Status" value={filters.status} options={facets.statuses} onChange={(v) => { setPage(1); setFilters({ ...filters, status: v }); }} />
            <FilterSelect label="Niche" value={filters.niche} options={facets.niches} onChange={(v) => { setPage(1); setFilters({ ...filters, niche: v }); }} />
            <FilterSelect label="City" value={filters.city} options={facets.cities} onChange={(v) => { setPage(1); setFilters({ ...filters, city: v }); }} />
            <FilterSelect label="State" value={filters.state} options={facets.states} onChange={(v) => { setPage(1); setFilters({ ...filters, state: v }); }} />
            <FilterSelect label="Source" value={filters.source} options={facets.sources} onChange={(v) => { setPage(1); setFilters({ ...filters, source: v }); }} />
            <FilterSelect label="Website issue" value={filters.website_issue} options={facets.website_issues} onChange={(v) => { setPage(1); setFilters({ ...filters, website_issue: v }); }} />
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-white/55">Min score</Label>
              <Input type="number" min={0} max={100} value={filters.min_score} onChange={(e) => { setPage(1); setFilters({ ...filters, min_score: e.target.value }); }} className="mt-1 bg-[#0A0A0A] border-white/15 text-white h-10 rounded-md" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-white/55">Max score</Label>
              <Input type="number" min={0} max={100} value={filters.max_score} onChange={(e) => { setPage(1); setFilters({ ...filters, max_score: e.target.value }); }} className="mt-1 bg-[#0A0A0A] border-white/15 text-white h-10 rounded-md" />
            </div>
            <div className="col-span-2 md:col-span-4 flex justify-end">
              <button
                onClick={() => { setPage(1); setFilters({ status: "", niche: "", city: "", state: "", source: "", website_issue: "", min_score: "", max_score: "", archived: filters.archived }); }}
                className="text-xs text-white/50 hover:text-white transition-colors"
              >
                Clear filters
              </button>
            </div>
          </div>
        )}

        {selected.size > 0 && (
          <div className="mt-4 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5 flex flex-wrap items-center gap-3">
            <span className="text-sm text-white/80" data-testid="selected-count">
              {selected.size} selected
            </span>
            <Select onValueChange={(v) => bulk({ status: v })}>
              <SelectTrigger className="h-9 w-56 bg-[#0A0A0A] border-white/15 text-white text-sm">
                <SelectValue placeholder="Change status…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                {facets.statuses.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => bulk({ archive: !filters.archived })} className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-9" data-testid="bulk-archive">
              {filters.archived ? <><ArchiveRestore size={13} className="mr-1.5" /> Restore</> : <><Archive size={13} className="mr-1.5" /> Archive</>}
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportCSV(true)} className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-9">
              <Download size={13} className="mr-1.5" /> Export selected
            </Button>
            <Button size="sm" variant="outline" onClick={confirmBulkDelete} className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-200 rounded-md h-9" data-testid="bulk-delete">
              <Trash2 size={13} className="mr-1.5" /> Delete
            </Button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-white/50 hover:text-white transition-colors">
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-soft rounded-2xl overflow-hidden" data-testid="crm-table-wrapper">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] border-b border-white/10">
              <tr>
                <th className="w-10 px-4 py-3 text-left">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                    data-testid="select-all"
                  />
                </th>
                {COLUMNS.map((c) => (
                  <th key={c.key} style={{ minWidth: c.minW }} className={`px-3 py-3 text-left text-[11px] uppercase tracking-[0.18em] text-white/50 font-medium whitespace-nowrap ${c.sortable ? "cursor-pointer hover:text-white" : ""}`} onClick={() => toggleSort(c)}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.sortable && sortBy === c.key && (sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                    </span>
                  </th>
                ))}
                <th className="w-10 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length + 2} className="px-4 py-16 text-center text-white/50" data-testid="crm-loading">
                    <Loader2 className="inline-block mr-2 animate-spin" size={16} /> Loading leads…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 2} className="px-4 py-16 text-center text-white/40" data-testid="crm-empty">
                    No leads match your filters yet.
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`crm-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggleOne(r.id)}
                      data-testid={`select-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-white truncate max-w-[240px]" title={r.business_name}>
                    <Link to={`/admin/dashboard/leads/${r.id}`} className="hover:text-blue-300 font-medium">
                      {r.business_name || "—"}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-white/70">{r.contact_name || "—"}</td>
                  <td className="px-3 py-3 text-white/70">{r.niche || "—"}</td>
                  <td className="px-3 py-3 text-white/70 whitespace-nowrap">{r.phone || "—"}</td>
                  <td className="px-3 py-3 text-white/70 truncate max-w-[220px]" title={r.email}>{r.email || "—"}</td>
                  <td className="px-3 py-3 text-white/70 truncate max-w-[220px]">
                    {r.website ? (
                      <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-300 inline-flex items-center gap-1">
                        {r.website.replace(/^https?:\/\//, "")}
                        <ExternalLink size={11} />
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-3 text-white/70">{r.city || "—"}</td>
                  <td className="px-3 py-3 text-white/70">{r.state || "—"}</td>
                  <td className="px-3 py-3 text-white">{r.lead_score ?? "—"}</td>
                  <td className="px-3 py-3 text-white/70">{r.lead_source || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-block text-[11px] px-2.5 py-1 rounded-full border ${STATUS_COLORS[r.status] || "bg-white/5 text-white/70 border-white/15"}`}>
                      {r.status || "New Lead"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-white/50 whitespace-nowrap text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => setEditing(r)} className="text-white/40 hover:text-white transition-colors" title="Edit" data-testid={`edit-${r.id}`}>
                      <ExternalLink size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-white/10">
          <div className="text-xs text-white/50">
            Showing {rows.length ? (page - 1) * pageSize + 1 : 0}–{(page - 1) * pageSize + rows.length} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-9 disabled:opacity-40" data-testid="pagination-prev">
              <ChevronLeft size={14} className="mr-1" /> Prev
            </Button>
            <span className="text-xs text-white/60 px-2">
              {page} / {totalPages}
            </span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-9 disabled:opacity-40" data-testid="pagination-next">
              Next <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <LeadFormDialog
        open={creating || !!editing}
        onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}
        initial={editing}
        onSaved={async () => { setCreating(false); setEditing(null); await load(); }}
        statuses={facets.statuses}
      />
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-white/55">{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/15 text-white h-10 rounded-md">
          <SelectValue placeholder={`All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent className="bg-[#0F0F0F] border-white/10 text-white max-h-60">
          <SelectItem value="__all__">All</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function LeadFormDialog({ open, onOpenChange, initial, onSaved, statuses }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(initial ? { ...initial } : {
      business_name: "", contact_name: "", niche: "", phone: "", email: "",
      website: "", city: "", state: "", website_issue: "", lead_source: "Manual",
      status: "New Lead", lead_score: "", estimated_deal_value: "", notes: "",
      next_follow_up: "",
    });
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      // Convert numeric strings
      ["lead_score", "estimated_deal_value"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      if (initial?.id) {
        await api.patch(`/admin/leads/${initial.id}`, payload);
        toast.success("Lead updated.");
      } else {
        await api.post("/admin/leads", payload);
        toast.success("Lead created.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0F0F0F] border border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial ? "Edit lead" : "Add lead manually"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            {initial ? "Update lead details." : "Enter what you know. Duplicates by phone / email / website / business name are blocked automatically."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <F l="Business name"><Input value={form.business_name || ""} onChange={set("business_name")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Contact name"><Input value={form.contact_name || ""} onChange={set("contact_name")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Niche"><Input value={form.niche || ""} onChange={set("niche")} placeholder="e.g. Auto Detailing" className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Phone"><Input value={form.phone || ""} onChange={set("phone")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Email"><Input value={form.email || ""} onChange={set("email")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Website"><Input value={form.website || ""} onChange={set("website")} placeholder="example.com" className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="City"><Input value={form.city || ""} onChange={set("city")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="State"><Input value={form.state || ""} onChange={set("state")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Website issue"><Input value={form.website_issue || ""} onChange={set("website_issue")} placeholder="Outdated design, no HTTPS…" className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Lead source"><Input value={form.lead_source || ""} onChange={set("lead_source")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Status">
            <Select value={form.status || "New Lead"} onValueChange={set("status")}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/15 text-white h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white max-h-60">
                {(statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F l="Lead score (0-100)"><Input type="number" min={0} max={100} value={form.lead_score ?? ""} onChange={set("lead_score")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Estimated deal value ($)"><Input type="number" min={0} value={form.estimated_deal_value ?? ""} onChange={set("estimated_deal_value")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
          <F l="Next follow-up">
            <Input type="datetime-local" value={form.next_follow_up ? form.next_follow_up.slice(0, 16) : ""} onChange={(e) => setForm((f) => ({ ...f, next_follow_up: e.target.value ? new Date(e.target.value).toISOString() : "" }))} className="bg-[#0A0A0A] border-white/15 text-white h-10" />
          </F>
          <F l="Notes" full>
            <Textarea value={form.notes || ""} onChange={set("notes")} rows={4} className="bg-[#0A0A0A] border-white/15 text-white" />
          </F>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
            Cancel
          </Button>
          <Button onClick={save} disabled={busy} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="lead-form-save">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ l, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-white/55">{l}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
